import { BoundingInfo } from '@babylonjs/core/Culling/boundingInfo'
import { Ray } from '@babylonjs/core/Culling/ray'
import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh'
import { Mesh } from '@babylonjs/core/Meshes/mesh'
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { Tools } from '@babylonjs/core/Misc/tools'
import AnimationTimer from '../AnimationTimer'
import { replaceFinish } from '../ConfigurationInterpreter'
import { EngravingMaterial, loadEngravingFont } from '../Engraving'
import { TrackException } from '../Telemetry'
import Part from './Part'

export default class Temple extends Part {
	engraving: EngravingMaterial

	decal: Mesh

	constructor(parent: TransformNode, mirror: boolean = false) {
		super('TEMPLE', parent, mirror)
		this.mirror = mirror
	}

	override get isPrecious(): boolean {
		return ['TEMPLE'].some(x => this.config.components[x].color?.startsWith('AU750') || this.config.components[x].color?.startsWith('PT950'))
	}

	get isKidTeen(): boolean {
		return this.config.productline === 'RIMKID' || this.config.productline === '1000TEEN'
	}

	override get filename(): string {
		const modelType = this.isPrecious ? 'Precious' : this.isKidTeen ? 'Kids' : 'Titanium'
		return this.config.components.TEMPLE ? `Temples/${modelType}/${[this.config.components.TEMPLE.design, this.config.components.TEMPLE.size].filter(x => x).join(' ')}` : null
	}

	override getMeshMaterialName(mesh: AbstractMesh): string {
		const detail = /^(((TEMPLE|WIRE|PLATE)-DETAIL)|TUBE|WIRE-INNER)(-[A-Z0-9]{2,})?$/
		const base = /^(TEMPLE|WIRE|PLATE|TITANIUM)(-[A-Z0-9]{2,})?$/
		const gemstone = /^GEMSTONE|DIAMOND(-[0-9]{1,})?$/

		if (detail.test(mesh.name)) {
			const finish = mesh.name.match(detail)[4]?.substring(1)
			console.log(finish)
			return finish ? replaceFinish(this.config.components.TEMPLE_DETAIL?.color || this.config.components.TEMPLE.color, finish) : this.config.components.TEMPLE_DETAIL?.color || this.config.components.TEMPLE.color
		} else if (mesh.name == 'TEMPLE-HORN') {
			return this.config.components.TEMPLE_HORN.color
		} else if (base.test(mesh.name)) {
			const finish = mesh.name.match(base)[2]?.substring(1)
			return finish ? replaceFinish(this.config.components.TEMPLE.color, finish) : this.config.components.TEMPLE.color
		} else if (/^COVER|ACETATE|TEMPLE-ACETATE$/.test(mesh.name)) {
			return this.config.components.COVER?.color || this.config.components.TEMPLE_ACETATE.color
		} else if (mesh.name == 'TEMPLE-HORN') {
			return this.config.components.TEMPLE_HORN.color
		} else if (mesh.name == 'SNAP-ON') {
			return this.config.components.SNAP_ON.color
		} else if (mesh.name == 'TIP') {
			return this.config.components.TEMPLE_TIP.color
		} else if (gemstone.test(mesh.name)) {
			const gemstoneIndex = parseInt(mesh.name.match(gemstone)[1]?.substring(1) || '0')
			return gemstoneIndex ? this.config.components[`GEMSTONE-${gemstoneIndex}`]?.color || this.config.components.GEMSTONE?.color : this.config.components.GEMSTONE?.color || 'DIAMOND/W'
		} else {
			console.error(mesh.name)
			throw (`Unknown element: (${mesh.name})`)
		}
	}

	override async preloadMaterials() {
		super.preloadMaterials()
		if (!this.config.disableEngraving) {
			this.engraving = this.getScene().getMaterialByName('ENGRAVING-' + (this.mirror ? 'RIGHT' : 'LEFT')) as EngravingMaterial || new EngravingMaterial('ENGRAVING-' + (this.mirror ? 'RIGHT' : 'LEFT'), this.getScene())
			// TODO: RESET METADATA AGE
			await loadEngravingFont()
		}
	}

	override swap(animated?: boolean): void {
		super.swap(animated)
		try {
			if (this.loadedFilename && this.config.engraving != null) this.addEngraving() // TODO: Fix Synchronous
		} catch (error) {
			TrackException(error, { type: 'CreateEngraving' })
			console.warn('Unable to create engraving', error)
		}
	}

	addEngraving() {
		if (this.config.disableEngraving) return
		this.decal?.dispose(false, false)
		const ratio = 10 / 1
		if (this.mirror) {
			this.engraving.drawLogo(false, ratio)
		} else {
			this.engraving.drawText(this.config.engraving || '', false, ratio)
		}
		const bounds = this.getHierarchyBoundingVectors(true)
		const info = new BoundingInfo(bounds.min, bounds.max)

		let zStart: number
		let zEnd: number
		const ends = this.getChildMeshes().find(x => x.name == 'SNAP-ON' || x.name == 'TIP')
		if (ends) {
			zStart = this.getAbsolutePosition().z
			zEnd = ends.getBoundingInfo().boundingBox.maximumWorld.z
			// ends.showBoundingBox = true
		} else {
			zStart = info.boundingBox.minimumWorld.z * .7
			zEnd = info.boundingBox.maximumWorld.z
			console.warn('MISSING SNAP-ON OR TIP')
		}
		const zPosition = zStart + ((zEnd - zStart) / 2)
		const [top, bottom] = this.getTempleBounds(this, info, zPosition)

		let size = Math.min((top - bottom) * ratio * .8, .0012 * ratio)
		// console.log("Engraving size", (size * 100).toFixed(1))

		const pos = new Vector3(this.mirror ? info.boundingBox.maximumWorld.x : info.boundingBox.minimumWorld.x, bottom + ((top - bottom) / 2), zPosition)
		const ray = new Ray(pos, this.mirror ? Vector3.Left() : Vector3.Right(), info.boundingBox.maximumWorld.x - info.boundingBox.minimumWorld.x)
		const pickInfo = this.getScene().pickWithRay(ray, x => this.getChildMeshes(false).includes(x), false)
		// RayHelper.CreateAndShow(ray, this.getScene(),  new Color3(0, 0, 1))

		// TODO: Avoid recreating
		this.decal = MeshBuilder.CreateDecal('ENGRAVING-' + (this.mirror ? 'RIGHT' : 'LEFT'), pickInfo.pickedMesh, {
			position: pickInfo.pickedPoint,
			normal: pickInfo.getNormal(true),
			size: new Vector3(size, size / ratio, size / ratio),
			localMode: true,
			cullBackFaces: true
		})
		this.decal.position.x = -0.00001
		this.decal.isPickable = false
		this.decal.material = this.engraving
	}

	getTempleBounds(temple: Part, info: BoundingInfo, z: number): number[] {
		// const bounds = temple.getHierarchyBoundingVectors(true)
		// const info = new BoundingInfo(bounds.min, bounds.max)
		const start = performance.now()
		const children = temple.getChildMeshes(false)

		let isHit = false
		let top: number, bottom: number
		for (let y = info.boundingBox.maximumWorld.y; y > info.boundingBox.minimumWorld.y; y -= 0.0001) {
			const pos = new Vector3(info.boundingBox.minimumWorld.x, y, z)
			const ray = new Ray(pos, Vector3.Right(), info.boundingBox.maximumWorld.x - info.boundingBox.minimumWorld.x)
			const pick = this.getScene().pickWithRay(ray, x => children.includes(x), true)
			//  RayHelper.CreateAndShow(ray, this.getScene(), pick?.hit ? new Color3(1, 0, 0) : new Color3(0, 1, 0));
			if (!isHit && pick.hit) {
				top = y
				isHit = true
			} else if (isHit && !pick.hit) {
				bottom = y
				y = info.boundingBox.minimumWorld.y
			}
		}
		// console.log("height:", ((top - bottom) * 1000).toFixed(1), "mm", `Execution time: ${performance.now() - start} ms`);
		return [top, bottom]
	}

	close(animated: boolean = true) {
		if (!this.config.closingAngles) return
		const angle = Tools.ToRadians(this.config.closingAngles[this.mirror ? 0 : 1]) * (this.mirror ? -1 : 1)
		if (animated) {
			setTimeout(() => new AnimationTimer(this.getScene(), { target: this.rotation, property: 'y', from: this.rotation.y, to: angle }), this.mirror ? 0 : 250)
		} else {
			this.rotation.y = angle
		}
	}

	open(animated: boolean = true) {
		if (animated) {
			setTimeout(() => new AnimationTimer(this.getScene(), { target: this.rotation, property: 'y', from: this.rotation.y, to: 0 }), this.mirror ? 100 : 0)
		} else {
			this.rotation.y = 0
		}
	}
}
